import classNames from "classnames";
import PropTypes from "prop-types";
import { isNil } from "lodash";
import format from "string-template";
import { CURRENCY_SYMBOL } from "utils/constants";
import Card from "./Card";
import { get } from "lodash";

const DUMMY_ICON = "https://icon-library.com/images/icon-url/icon-url-15.jpg";

const DPO_EMAIL = "dpo@mcci.fr";

const COSTS_LAYOUT = {
  stacked: "stacked",
  inlineOffers: "inlineOffers",
};

const InlineOfferCards = ({ costs }) => (
  <div className="quote-template-mcci__costs-row">
    {costs?.map(({ id, insurancePlan, cost, currency }) => {
      const symbol = CURRENCY_SYMBOL[currency] ?? CURRENCY_SYMBOL.EUR;
      return (
        <div className="quote-template-mcci__costs-row-card" key={id}>
          <img
            src={isNil(insurancePlan?.icon) ? DUMMY_ICON : insurancePlan.icon}
            alt={insurancePlan?.name ?? ""}
          />
          <div className="quote-template-mcci__costs-row-card-price">
            {cost} {symbol}
          </div>
          <div className="quote-template-mcci__costs-row-card-name">
            {insurancePlan?.name}
            <span className="quote-template-mcci__costs-row-card-desc">
              {insurancePlan?.description}
            </span>
          </div>
        </div>
      );
    })}
  </div>
);

InlineOfferCards.propTypes = {
  costs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      cost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      currency: PropTypes.string,
      insurancePlan: PropTypes.shape({
        icon: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
      }),
    }),
  ),
};

InlineOfferCards.defaultProps = {
  costs: [],
};

const EmailTemplate = ({
  civility,
  lastName,
  firstName,
  currentDate,
  endOfValidityDate,
  adminPhoneNumber,
  adminFirstName,
  adminLastName,
  adminEmail,
  adminAddress,
  translation,
  costs,
  addLinks,
  costsLayout,
  quoteUrl,
  brokerName,
  brokerOrias,
  brokerStatus,
  brokerAddress,
  organization,
}) => {
  const isInlineOffers = costsLayout === COSTS_LAYOUT.inlineOffers;
  const resolvedBrokerStatus =
    brokerStatus || translation.BROKER_STATUS_DEFAULT;
  const href = quoteUrl || "#";
  const legal2 = format(translation.LEGAL_2, {
    brokerName,
    brokerOrias,
    brokerStatus: resolvedBrokerStatus,
    brokerAddress,
  });

  return (
    <div className="quote-template-mcci">
      <div className="quote-template-mcci__main-container">
        <div className="quote-template-mcci__header">
          <img
            src={
              get(organization, "icon") ||
              "https://www.mcci.fr/wp-content/uploads/2023/07/MCCI_BLEU_ROUGE_UGO.svg"
            }
            alt={translation.LOGO_ALT}
          />
        </div>
        <div className="quote-template-mcci__container">
          <div className="quote-template-mcci__content">
            <p>
              {translation.HELLO} {translation[civility] || civility}{" "}
              {firstName} {lastName},
            </p>
            <p>{translation.P1}</p>
            <p>{format(translation.P2, { currentDate })}</p>
            <p>{translation.P3}</p>
            <p>{format(translation.P4, { endOfValidityDate })}</p>
          </div>
          <div className="quote-template-mcci__content">
            <a className="quote-template-mcci__submit-link" href={href}>
              <span className="quote-template-mcci__submit">
                {translation.BUTTON}
              </span>
            </a>
          </div>
          <div className={classNames({ hidden: !addLinks })}>
            <div className="quote-template-mcci__content">
              <p>
                <strong>{translation.P5}</strong>
              </p>
            </div>
            {isInlineOffers ? (
              <InlineOfferCards costs={costs} />
            ) : (
              costs?.map(({ id, insurancePlan, cost, currency }) => (
                <Card
                  key={id}
                  icon={insurancePlan?.icon}
                  name={insurancePlan?.name}
                  cost={cost}
                  currency={currency}
                  period={translation.PER_MONTH}
                  perYear={translation.PER_YEAR}
                  allIncluded={translation.ALL_INCLUDED}
                  link={translation.LINK}
                />
              ))
            )}
          </div>
          <div className="quote-template-mcci__content">
            <p>{format(translation.P6, { adminPhoneNumber })}</p>
          </div>
          <div className="quote-template-mcci__content">
            <p>{translation.P7}</p>
          </div>
          <div className="quote-template-mcci__sign-off">
            <p>{translation.SIGN_OFF_SEP}</p>
          </div>
          <div className="quote-template-mcci__advisor">
            <p>
              {format(translation.YOUR_CONSULTANT, {
                brokerName,
              })}
            </p>
            <p>
              {adminLastName} {adminFirstName}
            </p>
            <p>{adminPhoneNumber}</p>
            <p>{adminEmail}</p>
            <p>{adminAddress}</p>
          </div>
          <div className="quote-template-mcci__legal-block">
            <p className="quote-template-mcci__legal">{translation.LEGAL_1}</p>
            <p className="quote-template-mcci__legal">{legal2}</p>
            <p className="quote-template-mcci__legal">{translation.LEGAL_3}</p>
            <p className="quote-template-mcci__legal">
              {translation.LEGAL_4_BEFORE}
              <a
                className="quote-template-mcci__legal-link"
                href={`mailto:${DPO_EMAIL}`}
              >
                {DPO_EMAIL}
              </a>
              {translation.LEGAL_4_AFTER}
            </p>
            <p className="quote-template-mcci__legal">{translation.LEGAL_5}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

EmailTemplate.propTypes = {
  civility: PropTypes.string,
  lastName: PropTypes.string,
  firstName: PropTypes.string,
  currentDate: PropTypes.string,
  endOfValidityDate: PropTypes.string,
  adminPhoneNumber: PropTypes.string,
  adminFirstName: PropTypes.string,
  adminLastName: PropTypes.string,
  adminEmail: PropTypes.string,
  adminAddress: PropTypes.string,
  translation: PropTypes.object.isRequired,
  costs: PropTypes.array,
  addLinks: PropTypes.bool,
  costsLayout: PropTypes.oneOf(Object.values(COSTS_LAYOUT)),
  quoteUrl: PropTypes.string,
  brokerName: PropTypes.string,
  brokerOrias: PropTypes.string,
  brokerStatus: PropTypes.string,
};

EmailTemplate.defaultProps = {
  civility: "",
  lastName: "",
  firstName: "",
  currentDate: "",
  endOfValidityDate: "",
  adminPhoneNumber: "",
  adminFirstName: "",
  adminLastName: "",
  adminEmail: "",
  adminAddress: "",
  costs: [],
  addLinks: false,
  costsLayout: COSTS_LAYOUT.stacked,
  quoteUrl: "",
  brokerName: "",
  brokerOrias: "",
  brokerStatus: "",
};

export default EmailTemplate;
export { COSTS_LAYOUT };
